import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getBacNETData } from '../../redux/actions/otActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import OTDataTable from '../../components/OTDataTable';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import BacNETStyles from './BacNET.module.css';

const FRONTEND_BACNET_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildBacNETSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({ errorCode, pageInfo, sourceMessage });

const BacNETScreen = () => {
	const dispatch = useDispatch();

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ bacNETData, setBacNETData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setBacNETSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildBacNETSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearBacNETSupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearBacNETSupportError();
				try {
					const data = await dispatch(getBacNETData());
					if (data && data.status && Array.isArray(data.BacNETRequestCols) && Array.isArray(data.BacNETRequestRows)) {
						setBacNETData(data);
						clearBacNETSupportError();
					} else {
						setBacNETData({});
						setBacNETSupportError(
							FRONTEND_BACNET_ERROR_CODES.INITIAL_LOAD_FAILED,
							'BacNET Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'BacNET request did not return success')
						);
					}
				} catch (error) {
					setBacNETData({});
					setBacNETSupportError(
						FRONTEND_BACNET_ERROR_CODES.INITIAL_LOAD_FAILED,
						'BacNET Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, clearBacNETSupportError, setBacNETSupportError ]
	);

	return (
		<Content currentMenu='OT' headerTitle='BacNET Data'>
			{dataLoading ? (
				<Loader />
			) : bacNETData && Object.keys(bacNETData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={BacNETStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={BacNETStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={BacNETStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={BacNETStyles.supportErrorBanner}>
							<div className={BacNETStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={BacNETStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={BacNETStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					<OTDataTable
						cols={bacNETData.BacNETRequestCols}
						rows={bacNETData.BacNETRequestRows}
						containerStyle={{ height: '75vh' }}
					/>
				</Fragment>
			)}
		</Content>
	);
};

export default BacNETScreen;
